import DataType from '@opsbase/lib-datatype/DataType';
import i18n from '@opsbase/lib-i18n';
import { useStaffCollection } from '@opsbase/react-gql-hooks/staff/useStaff';
import DataChooser from '@opsbase/web-data-choosers/DataChooser';
import Card from '@opsbase/web-layout/Card';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import Switch from '@opsbase/web-ui/Switch';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useMemo } from 'react';
export function WhoSettingsSection(props) {
    var _a, _b, _c, _d;
    const { definition, isEditable = false, onChange, simpleLayout = false } = props;
    const skipStaffCheck = !definition || (definition === null || definition === void 0 ? void 0 : definition.allPTsPermitted);
    const { loading, data } = useStaffCollection({
        variables: {
            teamOrPosition: [
                ...((_b = (_a = definition.teams) === null || _a === void 0 ? void 0 : _a.map(t => t.id)) !== null && _b !== void 0 ? _b : []),
                ...((_d = (_c = definition.positions) === null || _c === void 0 ? void 0 : _c.map(t => t.id)) !== null && _d !== void 0 ? _d : []),
            ],
        },
        skip: skipStaffCheck,
    });
    const staffCount = data === null || data === void 0 ? void 0 : data.totalCount;
    const handleFieldUpdate = useCallback(field => value => {
        onChange === null || onChange === void 0 ? void 0 : onChange({ [field]: value });
    }, [onChange]);
    const handleChangeApproval = useCallback(v => {
        const approvalStaff = v.filter(p => {
            var _a, _b;
            return ((_a = p === null || p === void 0 ? void 0 : p.type) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === DataType.STAFF ||
                ((_b = p === null || p === void 0 ? void 0 : p.__typename) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === DataType.STAFF;
        });
        const approvalTeams = v.filter(p => {
            var _a, _b;
            return ((_a = p === null || p === void 0 ? void 0 : p.type) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === DataType.TEAM ||
                ((_b = p === null || p === void 0 ? void 0 : p.__typename) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === DataType.TEAM;
        });
        const approvalPositions = v.filter(p => {
            var _a, _b;
            return ((_a = p === null || p === void 0 ? void 0 : p.type) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === DataType.POSITION ||
                ((_b = p === null || p === void 0 ? void 0 : p.__typename) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === DataType.POSITION;
        });
        onChange({
            approvalStaff,
            approvalTeams,
            approvalPositions,
        });
    }, [onChange]);
    const handleChangePermitted = useCallback(v => {
        const teams = v.filter(p => {
            var _a, _b;
            return ((_a = p === null || p === void 0 ? void 0 : p.type) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === DataType.TEAM ||
                ((_b = p === null || p === void 0 ? void 0 : p.__typename) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === DataType.TEAM;
        });
        const positions = v.filter(p => {
            var _a, _b;
            return ((_a = p === null || p === void 0 ? void 0 : p.type) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === DataType.POSITION ||
                ((_b = p === null || p === void 0 ? void 0 : p.__typename) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === DataType.POSITION;
        });
        onChange({
            teams,
            positions,
            allPTsPermitted: !(teams.length || positions.length),
        });
    }, [onChange]);
    const handleSelectAll = useCallback(allSelected => {
        if (allSelected) {
            onChange({ allPTsPermitted: true, teams: [], positions: [] });
        }
        else {
            onChange({ allPTsPermitted: false });
        }
    }, [onChange]);
    const content = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        return (React.createElement(Vertical, { paddingY: true, gutter: true, shrink: true },
            React.createElement(DataChooser, { disabled: !isEditable, multi: true, required: true, value: [...((_a = definition.teams) !== null && _a !== void 0 ? _a : []), ...((_b = definition.positions) !== null && _b !== void 0 ? _b : [])], type: [DataType.POSITION, DataType.TEAM], onChange: handleChangePermitted, allSelectable: true, allSelected: definition.allPTsPermitted, allSelectedLabel: i18n.t('all-teams-positions'), onSelectAll: handleSelectAll }),
            !skipStaffCheck && !loading && staffCount === 0 && (React.createElement(Text, { color: "error" }, i18n.t('no-staff-in-teams-positions'))),
            !skipStaffCheck && !loading && staffCount > 0 && (React.createElement(Text, null, i18n.t('count-staff-in-teams-positions', { x: staffCount }))),
            React.createElement(Pane, { padding: true }),
            React.createElement(Switch, { disabled: !isEditable, checked: (_c = definition.approvalRequired) !== null && _c !== void 0 ? _c : false, onChange: handleFieldUpdate('approvalRequired'), label: i18n.t('op-approval-required') }),
            definition.approvalRequired && (React.createElement(DataChooser, { disabled: !isEditable, multi: true, selectLabel: i18n.t('select-who-op-approve'), value: [
                    ...((_d = definition.approvalStaff) !== null && _d !== void 0 ? _d : []),
                    ...((_e = definition.approvalTeams) !== null && _e !== void 0 ? _e : []),
                    ...((_f = definition.approvalPositions) !== null && _f !== void 0 ? _f : []),
                ], filters: { enabled: ['TRUE'] }, type: [DataType.STAFF, DataType.POSITION, DataType.TEAM], onChange: handleChangeApproval }))));
    }, [
        isEditable,
        definition.teams,
        definition.positions,
        definition.allPTsPermitted,
        definition.approvalRequired,
        definition.approvalStaff,
        definition.approvalTeams,
        definition.approvalPositions,
        handleChangePermitted,
        handleSelectAll,
        skipStaffCheck,
        loading,
        staffCount,
        handleFieldUpdate,
        handleChangeApproval,
    ]);
    if (simpleLayout)
        return content;
    return (React.createElement(Vertical, { gutter: true, padding: true },
        React.createElement(Horizontal, { gutter: true, justify: "flex-start", alignItems: "flex-bottom" },
            React.createElement(Text, { variant: "h1" }, i18n.t('who')),
            React.createElement(Text, { color: "grey" }, i18n.t('select-who-op-perform'))),
        React.createElement(Card, { padding: true }, content)));
}
export default WhoSettingsSection;
